import { API_CONFIG } from '@/lib/api/config';
import { cachedConnectionCheck, clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

// ==================== TYPES ====================

export type UberDirectEnvironment = 'sandbox' | 'production';

export interface UberDirectAddress {
  street_address: string[];
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

export interface UberDirectEndpoint {
  address: UberDirectAddress;
  name?: string;
  phone_number?: string;
  latitude?: number;
  longitude?: number;
}

export interface UberManifestItem {
  name: string;
  quantity: number;
  weight: number; // grams
  size?: string;
  price?: number;
}

export interface UberConnectPayload {
  business_account_id: string;
  environment: UberDirectEnvironment;
  customer_id: string;
  client_id: string;
  client_secret: string;
  webhook_signing_key?: string;
  account_label?: string;
}

export interface UberQuotePayload {
  business_account_id: string;
  pickup: UberDirectEndpoint;
  dropoff: UberDirectEndpoint;
}

export interface UberCreateDeliveryPayload {
  business_account_id: string;
  quote_id?: string;
  pickup: UberDirectEndpoint;
  dropoff: UberDirectEndpoint;
  manifest_items: UberManifestItem[];
  external_id?: string;
  tip?: number;
  undeliverable_action?: 'leave_at_door' | 'return' | 'discard';
  pickup_notes?: string;
  dropoff_notes?: string;
  manifest_reference?: string;
}

export interface UberListParams {
  limit?: number;
  offset?: number;
  status?: string;
}

export interface UberCourierLocation {
  lat?: number;
  lng?: number;
}

// Courier vehicle sub-object — Uber nests vehicle attributes under
// `courier.vehicle: { make, model, color, type }` on the live delivery object.
export interface UberCourierVehicle {
  make?: string;
  model?: string;
  color?: string;
  type?: string;
  [key: string]: unknown;
}

export interface UberCourier {
  name?: string;
  phone_number?: string;
  // Live shape nests vehicle attributes under `vehicle`; flat fallbacks kept
  // for backward-compat with payloads that flattened them at the top level.
  vehicle?: UberCourierVehicle | null;
  vehicle_type?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_color?: string;
  location?: UberCourierLocation;
  [key: string]: unknown;
}

// Structured address Uber returns inside a waypoint's `detailed_address`.
export interface UberDetailedAddress {
  street_address_1?: string;
  street_address_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  [key: string]: unknown;
}

export interface UberDeliveryWaypoint {
  name?: string;
  phone_number?: string;
  // `address` is normally a flat human-readable string, but the renderer also
  // tolerates a nested structured object for resilience.
  address?: string | Record<string, unknown>;
  detailed_address?: UberDetailedAddress | Record<string, unknown>;
  location?: UberCourierLocation;
  status?: string;
  notes?: string;
  eta?: string;
  [key: string]: unknown;
}

export interface UberManifestDimensions {
  length?: number;
  height?: number;
  depth?: number;
  [key: string]: unknown;
}

export interface UberDeliveryManifestItem {
  name?: string;
  quantity?: number;
  weight?: number;
  size?: string;
  price?: number;
  dimensions?: UberManifestDimensions;
  [key: string]: unknown;
}

export interface UberDeliveryManifest {
  description?: string;
  total_value?: number;
  [key: string]: unknown;
}

// Full live Uber delivery object returned by GET /deliveries/:id.
// NOTE: the backend wraps this under `{ success, delivery }`; the
// `getDelivery` client method UNWRAPS it so callers read top-level fields.
export interface UberDeliveryDetail {
  id?: string;
  delivery_id?: string;
  quote_id?: string;
  kind?: string;
  status?: string;
  complete?: boolean;
  fee?: number;
  currency?: string;
  tracking_url?: string;
  courier_imminent?: boolean;
  pickup_eta?: string;
  dropoff_eta?: string;
  undeliverable_reason?: string;
  courier?: UberCourier | null;
  pickup?: UberDeliveryWaypoint | null;
  dropoff?: UberDeliveryWaypoint | null;
  manifest?: UberDeliveryManifest | null;
  manifest_items?: UberDeliveryManifestItem[];
  created?: string;
  updated?: string;
}

export interface UberProofBarcode {
  value?: string;
  type?: string;
  scan_result?: string;
}

export interface UberProofSignature {
  image_url?: string;
  signer_name?: string;
}

export interface UberProofPinCode {
  entered?: string;
  status?: string;
}

export type UberProofWaypoint = 'pickup' | 'dropoff' | 'return';
export type UberProofType = 'picture' | 'signature' | 'pincode';

// Backend returns `proof: { document: "<base64 image string>" }` for
// picture/signature; for pincode the proof object may carry a code/status
// instead of an image. Every field is optional and the UI renders defensively.
export interface UberProofPayload {
  document?: string;
  code?: string;
  status?: string;
  [k: string]: unknown;
}

// Proof-of-delivery object — shape may vary; every field is optional and the
// UI renders defensively (a friendly "not available yet" message on failure).
export interface UberProofOfDelivery {
  proof?: UberProofPayload;
  signature?: UberProofSignature | null;
  picture?: string | null;
  barcodes?: UberProofBarcode[];
  pin_code?: UberProofPinCode | null;
}

// Swallow-error result shape — callers branch on `result.ok`, never try/catch.
// `error`/`code` are always present on the type (undefined on success) so callers
// can read them after an `if (!result.ok)` guard without union-narrowing friction.
export type UberDirectResult<T = Record<string, unknown>> = Partial<T> & {
  ok: boolean;
  error?: string;
  code?: string;
};

const handle = async <T = Record<string, unknown>>(
  res: Response,
  fallbackMessage: string
): Promise<UberDirectResult<T>> => {
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  // apiClient swallow-error pattern: backend may return { success:false, error } on 200
  if (!res.ok || json?.error || json?.success === false) {
    const error = (json?.error || json?.message || fallbackMessage) as string;
    const code = json?.code as string | undefined;
    return { ok: false, error, code } as UberDirectResult<T>;
  }
  return { ok: true, ...(json as T) };
};

export const uberDirectApi = {
  // ==================== CONNECTION ====================

  // Cached + in-flight-deduped for ~30s; cleared on connect/disconnect below.
  // `handle` returns { ok:false } on failure instead of throwing, so we evict a
  // failed status afterwards — a transient error must never stick in the cache.
  async getStatus(businessAccountId: string) {
    const result = await cachedConnectionCheck(`uber:${businessAccountId}`, async () => {
      const url = `${API_CONFIG.BASE_URL}/uber-direct/status?business_account_id=${encodeURIComponent(businessAccountId)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      return handle<{
        connected: boolean;
        environment?: UberDirectEnvironment;
        account_label?: string;
        source?: 'per_ba' | 'platform';
      }>(res, 'Failed to fetch Uber Direct status');
    });
    if (!(result as any)?.ok) clearConnectionCheckCache(`uber:${businessAccountId}`);
    return result;
  },

  async connect(payload: UberConnectPayload) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/uber-direct/connect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const result = await handle<{
      connected: boolean;
      environment?: UberDirectEnvironment;
    }>(res, 'Failed to connect Uber Direct');
    clearConnectionCheckCache('uber:');
    return result;
  },

  async disconnect(businessAccountId: string) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/uber-direct/disconnect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ business_account_id: businessAccountId }),
    });
    const result = await handle(res, 'Failed to disconnect Uber Direct');
    clearConnectionCheckCache('uber:');
    return result;
  },

  // ==================== QUOTE ====================

  async createQuote(payload: UberQuotePayload) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/uber-direct/quote`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handle<{
      quote_id: string;
      fee: number; // integer, smallest currency unit
      currency: string;
      dropoff_eta?: string;
      expires?: string;
      duration?: number;
      pickup_duration?: number;
    }>(res, 'Failed to create quote');
  },

  // ==================== DELIVERIES ====================

  async createDelivery(payload: UberCreateDeliveryPayload) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/uber-direct/deliveries`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handle<{
      id?: string;
      delivery_id?: string;
      quote_id?: string;
      status?: string;
      complete?: boolean;
      fee?: number;
      currency?: string;
      tracking_url?: string;
      pickup_eta?: string;
      dropoff_eta?: string;
    }>(res, 'Failed to create delivery');
  },

  async listDeliveries(businessAccountId: string, params?: UberListParams) {
    const query = new URLSearchParams();
    query.append('business_account_id', businessAccountId);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    if (params?.status) query.append('status', params.status);
    const url = `${API_CONFIG.BASE_URL}/uber-direct/deliveries?${query.toString()}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    return handle<{
      items?: unknown[];
      data?: unknown[];
      count?: number;
      total?: number;
      statusCounts?: Record<string, number>;
    }>(
      res,
      'Failed to list deliveries'
    );
  },

  async getDelivery(businessAccountId: string, deliveryId: string) {
    const url = `${API_CONFIG.BASE_URL}/uber-direct/deliveries/${encodeURIComponent(deliveryId)}?business_account_id=${encodeURIComponent(businessAccountId)}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    // Backend wraps the full live Uber object under `{ success, delivery }`.
    // UNWRAP it so callers read top-level fields (status, pickup, courier, …)
    // — without this the detail modal reads result.status etc. as undefined and
    // every field renders "—" (the empty-modal bug). Keep the swallow-error
    // pattern: on failure (or a missing `delivery`) return the result as-is.
    const result = await handle<{ delivery?: UberDeliveryDetail }>(
      res,
      'Failed to refresh delivery'
    );
    if (result.ok && result.delivery && typeof result.delivery === 'object') {
      return { ok: true, ...(result.delivery as object) } as UberDirectResult<UberDeliveryDetail>;
    }
    return result as unknown as UberDirectResult<UberDeliveryDetail>;
  },

  async cancelDelivery(businessAccountId: string, deliveryId: string) {
    const res = await fetch(
      `${API_CONFIG.BASE_URL}/uber-direct/deliveries/${encodeURIComponent(deliveryId)}/cancel`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ business_account_id: businessAccountId }),
      }
    );
    return handle<{ status?: string }>(res, 'Failed to cancel delivery');
  },

  // Uber's /proof-of-delivery REQUIRES both `waypoint` and `type` — omitting
  // either 400s with "Waypoint cannot be recognized". Defaults: dropoff/picture.
  async getProofOfDelivery(
    businessAccountId: string,
    deliveryId: string,
    opts?: { waypoint?: UberProofWaypoint; type?: UberProofType }
  ) {
    const res = await fetch(
      `${API_CONFIG.BASE_URL}/uber-direct/deliveries/${encodeURIComponent(deliveryId)}/proof-of-delivery`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          business_account_id: businessAccountId,
          waypoint: opts?.waypoint || 'dropoff',
          type: opts?.type || 'picture',
        }),
      }
    );
    return handle<UberProofOfDelivery>(res, 'Failed to fetch proof of delivery');
  },
};
